import React, { useCallback, useMemo, useState } from 'react';

import { Confirm, Modal } from '@components/common';
import Table from '@components/common/Table';

import { useDragAndDropState } from '@hooks/common/useDragAndDropState';
import useMutateMiddlewareDisableLogPM from '@hooks/middlewareSMO/useMutateMiddlewareDisableLogPM';
import useMutateMiddlewareEnableLogPM from '@hooks/middlewareSMO/useMutateMiddlewareEnableLogPM';
import useMutateMiddlewareSMODisable from '@hooks/middlewareSMO/useMutateMiddlewareSMODisable';
import useMutateMiddlewareSMOEnable from '@hooks/middlewareSMO/useMutateMiddlewareSMOEnable';
import useMutateMiddlewareSMORemove from '@hooks/middlewareSMO/useMutateMiddlewareSMORemove';
import useQueryMiddlewareSMO from '@hooks/middlewareSMO/useQueryMiddlewareSMO';
import useQueryMiddlewareSMOStatuses from '@hooks/middlewareSMO/useQueryMiddlewareSMOStatuses';

import { auth } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	ISortBy,
	ITablePagination,
	ITableSearch,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';
import { IMiddlewareSMO } from '@interfaces/middlewareSMO/middlewareSMO';

import { defaultMeta } from '@enums/common';

import { MIN_FILTER_WIDTH } from '@constants/common';
import { MODAL_WIDTH_580 } from '@constants/modals';
import {
	DELETE_TEXT,
	DISABLE_PM_FILES_STORING,
	DISABLE_TEXT,
	ENABLE_PM_FILES_STORING,
	ENABLE_TEXT,
} from '../constants';

import { getRowItemId } from 'components/common/Table/helpers';

import { getColumns } from '../utils';

import StorePMFilesForm from '../StorePMFilesForm';

import styles from '../ManageMiddlewareSMO.module.scss';

const MiddlewaresTable = () => {
	const {
		accessPermission: {
			smoLightUpdater,
			smoLightViewer,
		},
		activeOrganisation: organizationId,
	} = useAppSelector(auth);

	const [selectedMiddleware, setSelectedMiddleware] = useState<IMiddlewareSMO | null>(null);
	const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false);
	const [isOpenStorePMFilesForm, setIsOpenStorePMFilesForm] = useState(false);
	const [confirmationText, setConfirmationText] = useState('');

	const [page, setPage] = useState(defaultMeta.page);
	const [pageSize, setPageSize] = useState(defaultMeta.pageSize);
	const [sortDirection, setSortDirection] = useState(false);
	const [sortFieldName, setSortFieldName] = useState<string | null>(null);
	const [searchValue, setSearchValue] = useState('');
	const [selectedStatus, setSelectedStatus] = useState<TSelectOption<string> | null>(null);

	const {
		middlewareSMOData,
		countOfRecords,
		isLoadingMiddlewareSMO,
		allMiddlewareSMOData,
	} = useQueryMiddlewareSMO({
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
		status: selectedStatus?.value,
		organizationId,
	});

	const {
		reorderedData: reorderedMiddlewareSMOData,
		onRowReorder: handleRowReorderMiddlewareSMO,
		resetOrder: resetOrderMiddlewareSMO,
		hasCustomOrder: hasCustomOrderMiddlewareSMO,
	} = useDragAndDropState<IMiddlewareSMO>({
		allData: allMiddlewareSMOData,
		currentPageData: middlewareSMOData,
		page,
		pageSize,
		storageKey: 'smo-middleware-table-order',
		getItemId: getRowItemId,
		isEnabled: true,
	});

	const { middlewareStatuses, isLoadingMiddlewareStatuses } = useQueryMiddlewareSMOStatuses();

	const openConfirmModal = useCallback((middleware: IMiddlewareSMO | null, text: string) => {
		if (!middleware) {
			return;
		}

		setIsOpenConfirmModal((prevState) => !prevState);
		setConfirmationText(text);
		setSelectedMiddleware(middleware);
	}, []);

	const closeConfirmModal = useCallback(() => {
		setIsOpenConfirmModal(false);
		setSelectedMiddleware(null);
	}, []);

	const { disableMiddlewareSMO, isLoadingDisableMiddlewareSMO } = useMutateMiddlewareSMODisable(closeConfirmModal);
	const { enableMiddlewareSMO, isLoadingEnableMiddlewareSMO } = useMutateMiddlewareSMOEnable(closeConfirmModal);
	const { removeMiddlewareSMO, isLoadingRemoveMiddlewareSMO } = useMutateMiddlewareSMORemove(closeConfirmModal);
	const { enableMiddlewareLogPM, isLoadingEnableMiddlewareLogPM } = useMutateMiddlewareEnableLogPM(closeConfirmModal);
	const {
		disableMiddlewareLogPM,
		isLoadingDisableMiddlewareLogPM,
	} = useMutateMiddlewareDisableLogPM(closeConfirmModal);

	const isViewerOnly = !smoLightUpdater && smoLightViewer;

	const isLoadingConfirmModal = (
		isLoadingEnableMiddlewareSMO ||
		isLoadingDisableMiddlewareSMO ||
		isLoadingRemoveMiddlewareSMO ||
		isLoadingEnableMiddlewareLogPM ||
		isLoadingDisableMiddlewareLogPM
	);

	const isLoadingTable = isLoadingMiddlewareSMO || isLoadingMiddlewareStatuses;

	const toggleLogPM = useCallback((middleware: IMiddlewareSMO) => {
		const { id, logPmFiles } = middleware;

		if (logPmFiles) {
			disableMiddlewareLogPM(id);
		} else {
			enableMiddlewareLogPM(id);
		}
	}, [disableMiddlewareLogPM, enableMiddlewareLogPM]);

	const toggleMiddlewareStatus = useCallback((middleware: IMiddlewareSMO) => {
		const { id, isEnabled } = middleware;

		if (isEnabled) {
			disableMiddlewareSMO(id);
		} else {
			enableMiddlewareSMO(id);
		}
	}, [disableMiddlewareSMO, enableMiddlewareSMO]);

	const removeHandler = useCallback((middleware: IMiddlewareSMO) => {
		if (!middleware) {
			return;
		}

		removeMiddlewareSMO(middleware.id);
	}, [removeMiddlewareSMO]);

	const submitHandler = useCallback(() => {
		if (!selectedMiddleware || !confirmationText) return;

		switch (confirmationText) {
			case DELETE_TEXT:
				return removeHandler(selectedMiddleware);
			case ENABLE_TEXT:
			case DISABLE_TEXT:
				return toggleMiddlewareStatus(selectedMiddleware);
			case ENABLE_PM_FILES_STORING:
			case DISABLE_PM_FILES_STORING:
				return toggleLogPM(selectedMiddleware);
			default:
				return null;
		}
	}, [
		selectedMiddleware,
		confirmationText,
		removeHandler,
		toggleMiddlewareStatus,
		toggleLogPM,
	]);

	const openStorePMFiles = useCallback((middleware: IMiddlewareSMO | null) => {
		if (!middleware) {
			return;
		}

		setIsOpenStorePMFilesForm(true);
		setSelectedMiddleware(middleware);
	}, []);

	const closeStorePMFilesForm = useCallback(() => {
		setIsOpenStorePMFilesForm(false);
	}, []);

	const columns = useMemo(() => getColumns({
		openModal: openConfirmModal,
		isViewer: isViewerOnly,
		openStorePMFiles,
	}), [openConfirmModal, isViewerOnly, openStorePMFiles]);

	const searchHandler = useCallback((searchValue: string) => {
		setSearchValue(searchValue);
		setPage(defaultMeta.page);
	}, []);

	const pageHandler = useCallback((page: number) => setPage(page), []);
	const pageSizeHandler = useCallback((pageSize: number) => {
		setPageSize(pageSize);
		setPage(defaultMeta.page);
	}, []);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		setSortDirection(!!sortBy[0]?.desc);
		setSortFieldName(sortBy[0]?.id);
	}, []);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	const pagination: ITablePagination = useMemo(() => ({
		countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
		resetOrder: resetOrderMiddlewareSMO,
		hasCustomOrder: hasCustomOrderMiddlewareSMO,
	}), [
		page,
		pageSize,
		countOfRecords,
		pageHandler,
		pageSizeHandler,
		resetOrderMiddlewareSMO,
		hasCustomOrderMiddlewareSMO,
	]);

	const filters: TTableFilter[] = useMemo(() => [
		{
			name: 'status',
			placeholder: 'Status',
			option: selectedStatus,
			options: middlewareStatuses,
			minWidth: MIN_FILTER_WIDTH,
			setState: setSelectedStatus,
			defaultValue: '',
		},
	], [selectedStatus, middlewareStatuses]);

	const search: Array<ITableSearch> = useMemo(() => [{
		initialValue: searchValue,
		name: 'name',
		handler: searchHandler,
		tooltipText: 'Search by ID',
	}], [searchValue, searchHandler]);

	return (
		<>
			<Modal
				isConfirmDialogNeededOnClose
				isOpen={isOpenStorePMFilesForm}
				onClose={closeStorePMFilesForm}
				title='Log PM file'
				width={MODAL_WIDTH_580}
			>
				<StorePMFilesForm
					onClose={closeStorePMFilesForm}
					selectedMiddleware={selectedMiddleware}
				/>
			</Modal>
			<Modal
				isConfirmDialogNeededOnClose
				isOpen={isOpenConfirmModal}
				onClose={closeConfirmModal}
				width={MODAL_WIDTH_580}
			>
				<Confirm
					isLoading={isLoadingConfirmModal}
					onClose={closeConfirmModal}
					onSubmit={submitHandler}
					question={confirmationText}
				/>
			</Modal>
			<div className={styles.tableContainer}>
				<Table
					columns={columns}
					data={reorderedMiddlewareSMOData}
					dragHandle
					filters={filters}
					getRowId={getRowItemId}
					isDragAndDrop={countOfRecords > 1}
					isLoading={isLoadingTable}
					isOpenFilters={!!selectedStatus}
					isWithoutCheckbox
					isWithoutDeleteAction
					onRowReorder={handleRowReorderMiddlewareSMO}
					pagination={pagination}
					search={search}
					searchThreshold={0}
					sort={sort}
				/>
			</div>
		</>
	);
};

export default MiddlewaresTable;
